import { useEffect, useRef, useState } from "react";

export const STAGES = ["Buyer identification", "Buyer meeting", "Site visit", "Cost Negotiation",
  "Documents Diligence", "In Progress", "Final Agreement", "On Hold",
  "Closed Won", "Closed Lost", "Rejected"];
export const SOURCES = ["Advertisement", "Referral", "Website", "Walk-in", "Cold Call", "Existing Client", "Channel Partner"];
export const TXN_TYPES = ["Outright", "Joint Development", "Joint Venture", "Revenue Share"];
export const STRUCTURES = ["Plot", "Commercial Building", "Bungalow", "Apartments", "Hotel"];
export const PRIORITIES = ["High", "Medium", "Low"];
export const CLOSED = ["Closed Won", "Closed Lost", "Rejected"];
export const PAYMENT_STATUS_LIST = ["Unpaid", "Follow-Up", "Part-Paid", "Paid"];
export const PAYMENT_COLORS = {
  Unpaid: "text-neutral-400 border-white/15",
  "Follow-Up": "text-[#E6965C] border-[#E6965C]/40",
  "Part-Paid": "text-[#D9A441] border-[#D9A441]/40",
  Paid: "text-[#5C8A6E] border-[#5C8A6E]/40",
};
export const STAGE_WEIGHTS = {
  "Buyer identification": 0.1, "Buyer meeting": 0.2, "Site visit": 0.3, "Cost Negotiation": 0.5,
  "Documents Diligence": 0.6, "In Progress": 0.7, "Final Agreement": 0.85, "On Hold": 0.4,
  "Closed Won": 1, "Closed Lost": 0, "Rejected": 0,
};
export const STAGE_PALETTE = ["#335C4D", "#AE8438", "#D66B1D", "#4F6175", "#B82E62", "#5C8A6E", "#C9A24B", "#E08A55", "#74889E", "#C96E8F", "#43705D"];
export const PRIORITY_CHART_COLORS = { High: "#B82E62", Medium: "#D66B1D", Low: "#4F6175" };
export const PUNCHLINE = "You Dream… We Facilitate it!";

export const EMPTY_DEAL = {
  client_name: "", contact_person: "", phone: "", email: "", source: "Referral",
  priority: "Medium", stage: "Buyer identification", landowner_name: "", plot_no: "",
  landmark: "", address: "", structure: "Plot", transaction_type: "Outright",
  residential_sqft: "", commercial_sqft: "",
  rate_per_sqft_residential: "", rate_per_sqft_commercial: "",
  permissible_fsi: "", total_ask: "", probability: "", partner_code: "", partner_name: "",
  proposed_to: "", proposed_to_address: "", proposed_to_email: "", payment_status: "Unpaid",
  amount_received: 0, expected_fee_pct: "", notes: "",
};

export const inr = (v) => {
  const n = Number(v) || 0;
  if (n >= 1e7) return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n / 1e7)} Cr`;
  if (n >= 1e5) return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 }).format(n / 1e5)} L`;
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)}`;
};
export const num = (v) => Number(v) || 0;
export const fmtNum = (v) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(v || 0);

export const ageOf = (iso) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null);

const AGE_COLORS = {
  fresh: "text-neutral-400 border-white/15",
  warm: "text-[#E6965C] border-[#E6965C]/40",
  overdue: "text-[#F04864] border-[#F04864]/40",
};

export const waDealLink = (d) =>
  `https://wa.me/${(d.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(
    `Hello ${d.contact_person || d.client_name}, this is Keyscape Advisory Services regarding the ${d.client_name} mandate${d.plot_no ? ` (Plot ${d.plot_no})` : ""}. When would be a good time to speak?`
  )}`;

export const ageChip = (inv) => {  const age = ageOf(inv.invoice_date);
  if (age === null || inv.payment_status === "Paid") return null;
  const tone = age > 30 ? "overdue" : age > 15 ? "warm" : "fresh";
  return (
    <span className={`ml-2 px-2 py-0.5 border font-plexmono text-[9px] uppercase tracking-[0.1em] ${AGE_COLORS[tone]}`} data-testid={`inv-age-${inv.id}`}>
      {age === 0 ? "Today" : `${age}d old`}{tone === "overdue" ? " · Overdue" : ""}
    </span>
  );
};

export const PRIORITY_COLORS = {
  High: "text-[#F04864] border-[#F04864]/40",
  Medium: "text-[#E6965C] border-[#E6965C]/40",
  Low: "text-neutral-500 border-white/15",
};

export const BrandLockup = () => (
  <div className="flex items-center gap-3" data-testid="db-brand">
    <img src="/logo.jpeg" alt="Keyscape Advisory Services LLP" className="h-10 w-auto rounded-md bg-white px-1.5 py-0.5 object-contain" />
    <span className="leading-tight">
      <span className="block text-sm font-medium text-white">Keyscape Advisory Services LLP</span>
      <span className="block font-plexmono text-[9px] uppercase tracking-[0.3em] text-neutral-500">Clientele Pipeline</span>
      <span className="block text-[10px] italic text-[#D9A441] mt-0.5 tracking-wide" data-testid="brand-punchline">{PUNCHLINE}</span>
    </span>
  </div>
);

export const inputCls = "w-full bg-white/[0.03] border border-white/10 px-3 py-2.5 text-sm text-neutral-100 outline-none focus:border-[#D9A441] transition-colors rounded-none placeholder:text-neutral-600";
export const labelCls = "block font-plexmono text-[9px] uppercase tracking-[0.2em] text-neutral-500 mb-1.5";

export const Field = ({ label, children }) => (
  <label className="block">
    <span className={labelCls}>{label}</span>
    {children}
  </label>
);

export const ChartBox = ({ className, children }) => {
  const ref = useRef(null);
  const [size, setSize] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r && r.width > 0 && r.height > 0) setSize({ width: Math.floor(r.width), height: Math.floor(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return <div ref={ref} className={className}>{size ? children(size.width, size.height) : null}</div>;
};

export const KpiCard = ({ label, value, sub, accent, testid }) => (
  <div className={`border p-5 ${accent ? "border-[#D9A441]/40 bg-[#D9A441]/5" : "border-white/10 bg-white/[0.02]"}`} data-testid={testid}>
    <p className="font-plexmono text-[9px] uppercase tracking-[0.25em] text-neutral-500">{label}</p>
    <p className={`text-3xl font-light mt-3 ${accent ? "text-[#D9A441]" : "text-white"}`}>{value}</p>
    <p className="font-plexmono text-[9px] text-neutral-600 mt-2 uppercase tracking-[0.15em]">{sub}</p>
  </div>
);

const TIP_CLS = "border border-white/15 bg-[#111112] px-3 py-2 shadow-lg";
const TIP_LABEL_CLS = "font-plexmono text-[10px] uppercase tracking-[0.12em] text-neutral-500 m-0";
const TIP_VALUE_CLS = "text-[#D9A441] text-[13px] mt-0.5 mb-0";

export const StageTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={TIP_CLS} data-testid="chart-tip">
      <p className={TIP_LABEL_CLS}>{label}</p>
      <p className={TIP_VALUE_CLS}>{inr(payload[0].value)}</p>
    </div>
  );
};

export const PieTip = ({ active, payload, total }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const pct = total ? Math.round((p.value / total) * 100) : 0;
  return (
    <div className={TIP_CLS} data-testid="chart-tip">
      <p className="text-white text-xs m-0">{p.name}</p>
      <p className="text-[#D9A441] text-xs mt-0.5 mb-0">{inr(p.value)} · {pct}%</p>
    </div>
  );
};

export const MonthTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={TIP_CLS} data-testid="chart-tip">
      <p className={TIP_LABEL_CLS}>{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="text-xs mt-0.5 mb-0" style={{ color: p.color || "#fff" }}>{p.name}: {inr(p.value)}</p>
      ))}
    </div>
  );
};

export const Auto = ({ label, value, note, testId }) => (
  <div className="border border-white/10 bg-white/[0.02] px-3 py-2.5" data-testid={testId}>
    <p className="font-plexmono text-[9px] uppercase tracking-[0.2em] text-neutral-500">{label}</p>
    <p className="text-sm text-white mt-1 font-plexmono">{value}</p>
    {note && <p className="font-plexmono text-[8px] text-neutral-600 mt-0.5 uppercase tracking-[0.1em]">{note}</p>}
  </div>
);
